/**
 * Material-UI Palette Configuration
 *
 * Transforms raw color values from SCSS into Material-UI's expected palette structure.
 * This function creates a comprehensive color palette that supports both light and dark themes.
 *
 * @param {Object} theme - Theme customization object containing colors and mode
 * @param {Object} theme.colors - Raw color values from SCSS
 * @param {string} theme.mode - Current theme mode ('light' or 'dark')
 * @param {string} theme.paper - Paper/background color
 * @param {string} theme.backgroundDefault - Default background color
 * @param {string} theme.darkTextPrimary - Primary text color
 * @param {string} theme.darkTextSecondary - Secondary text color
 * @param {string} theme.textDark - Dark text color
 * @param {string} theme.heading - Heading text color
 * @param {string} theme.divider - Divider color
 *
 * @returns {Object} Material-UI palette object
 */
export default function themePalette(theme) {
  const isDark = theme.mode === "dark";

  return {
    // ==============================|| COMMON COLORS ||============================== //
    common: {
      black: theme.colors?.black || "#000000",
      white: theme.colors?.white || "#ffffff",
    },

    // ==============================|| PRIMARY & SECONDARY COLORS ||============================== //
    // Primary and secondary colors (complementary to semantic palette)
    primary: {
      light: theme.colors?.primaryLight,
      main: theme.colors?.primaryMain,
      dark: theme.colors?.primaryDark,
      200: theme.colors?.primary200,
      800: theme.colors?.primary800,
      contrastText: isDark ? theme.colors?.black : theme.colors?.white,
    },
    secondary: {
      light: theme.colors?.secondaryLight,
      main: theme.colors?.secondaryMain,
      dark: theme.colors?.secondaryDark,
      200: theme.colors?.secondary200,
      800: theme.colors?.secondary800,
      contrastText: isDark ? theme.colors?.black : theme.colors?.white,
    },

    // ==============================|| BRAND COLORS ||============================== //
    // Brand colors (original brand identity colors)
    brandPrimary: {
      light: theme.colors?.brandPrimaryLight,
      main: theme.colors?.brandPrimaryMain,
      dark: theme.colors?.brandPrimaryDark,
      200: theme.colors?.brandPrimary200,
      800: theme.colors?.brandPrimary800,
      contrastText: isDark ? theme.colors?.black : theme.colors?.white,
    },
    brandSecondary: {
      light: theme.colors?.brandSecondaryLight,
      main: theme.colors?.brandSecondaryMain,
      dark: theme.colors?.brandSecondaryDark,
      200: theme.colors?.brandSecondary200,
      800: theme.colors?.brandSecondary800,
      contrastText: isDark ? theme.colors?.black : theme.colors?.white,
    },

    // ==============================|| SEMANTIC COLORS ||============================== //
    error: {
      light: theme.colors?.errorLight,
      main: theme.colors?.errorMain,
      dark: theme.colors?.errorDark,
      contrastText: theme.colors?.white,
    },
    warning: {
      light: theme.colors?.warningLight,
      main: theme.colors?.warningMain,
      dark: theme.colors?.warningDark,
      contrastText: isDark ? theme.colors?.black : theme.colors?.white,
    },
    success: {
      light: theme.colors?.successLight,
      200: theme.colors?.success200,
      main: theme.colors?.successMain,
      dark: theme.colors?.successDark,
      contrastText: isDark ? theme.colors?.black : theme.colors?.white,
    },
    info: {
      light: theme.colors?.infoLight,
      main: theme.colors?.infoMain,
      dark: theme.colors?.infoDark,
      contrastText: isDark ? theme.colors?.black : theme.colors?.white,
    },

    // ==============================|| NEUTRAL COLORS ||============================== //
    grey: {
      50: theme.colors?.grey50,
      100: theme.colors?.grey100,
      200: theme.colors?.grey200,
      300: theme.colors?.grey300,
      400: theme.colors?.grey400,
      500: theme.darkTextSecondary,
      600: theme.heading,
      700: theme.darkTextPrimary,
      800: theme.colors?.grey800,
      900: theme.textDark,
    },

    // ==============================|| DARK THEME COLORS ||============================== //
    dark: {
      light: theme.colors?.darkTextPrimary,
      main: theme.colors?.darkLevel1,
      dark: theme.colors?.darkLevel2,
      800: theme.colors?.darkBackground,
      900: theme.colors?.darkPaper,
    },

    // ==============================|| TEXT COLORS ||============================== //
    text: {
      primary: theme.darkTextPrimary,
      secondary: theme.darkTextSecondary,
      disabled: isDark ? "rgba(255, 255, 255, 0.38)" : "rgba(0, 0, 0, 0.38)",
      hint: theme.colors?.grey100,
      light: theme.colors?.grey50,
      mid: theme.colors?.grey700,
      dark: theme.textDark,
    },

    // ==============================|| BACKGROUND COLORS ||============================== //
    background: {
      paper: theme.paper,
      default: theme.backgroundDefault,
    },

    // ==============================|| ACTION COLORS ||============================== //
    action: {
      active: isDark ? "rgba(255, 255, 255, 0.54)" : "rgba(0, 0, 0, 0.54)",
      hover: isDark ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.04)",
      hoverOpacity: 0.08,
      selected: isDark ? "rgba(255, 255, 255, 0.16)" : "rgba(0, 0, 0, 0.08)",
      selectedOpacity: 0.16,
      disabled: isDark ? "rgba(255, 255, 255, 0.3)" : "rgba(0, 0, 0, 0.26)",
      disabledBackground: isDark
        ? "rgba(255, 255, 255, 0.12)"
        : "rgba(0, 0, 0, 0.12)",
      disabledOpacity: 0.38,
      focus: isDark ? "rgba(255, 255, 255, 0.12)" : "rgba(0, 0, 0, 0.12)",
      focusOpacity: 0.12,
      activatedOpacity: 0.24,
    },

    // ==============================|| DIVIDER ||============================== //
    divider: theme.divider,

    // ==============================|| LEGACY SUPPORT ||============================== //
    // Keep orange for backward compatibility (using brand secondary)
    orange: {
      light: theme.colors?.orangeLight || theme.colors?.brandSecondaryLight,
      main: theme.colors?.orangeMain || theme.colors?.brandSecondaryMain,
      dark: theme.colors?.orangeDark || theme.colors?.brandSecondaryDark,
    },
  };
}
